/**
 * Erzeugt aus dem Beschwerdenkatalog je Beschwerdebild eine eigene FAQ-Frage
 * (DE + EN) – für crawlbaren Seiteninhalt und FAQPage-JSON-LD auf /faq.
 */
import { complaints, type Complaint } from "./complaints";
import { localizedComplaints } from "./complaints.en";

export interface ComplaintFaqEntry {
  slug: string;
  label: string;
  labelEn: string;
  q: string;
  qEn: string;
  a: string;
  aEn: string;
}

/** Erste n Sätze eines Textes, sauber abgeschlossen. */
const sentences = (text: string, n: number) => {
  const parts = text.replace(/\s+/g, " ").trim().match(/[^.!?]+[.!?]+/g);
  if (!parts) return text.trim();
  return parts.slice(0, n).join(" ").trim();
};

const trimEnd = (s: string) => s.replace(/\s*[;,]\s*$/, "").replace(/\.$/, "");

const buildDe = (c: Complaint): { q: string; a: string } => {
  const alias = c.synonyms.slice(0, 3).join(", ");
  return {
    q: `${c.shortTitle}: Kann hier geholfen werden?`,
    a: [
      sentences(c.intro, 2),
      c.approach.length
        ? `In der PRAXIS IB wird dabei so vorgegangen: ${trimEnd(sentences(c.approach[0], 1))}${
            c.approach[1] ? `; ${trimEnd(sentences(c.approach[1], 1))}` : ""
          }.`
        : "",
      alias ? `Häufig gesucht als: ${alias}.` : "",
      `Ausführlich beschrieben auf der Seite „${c.title}“ unter /beschwerden/${c.slug}.`,
    ]
      .filter(Boolean)
      .join(" "),
  };
};

const buildEn = (c: Complaint): { q: string; a: string } => {
  const alias = c.synonyms.slice(0, 3).join(", ");
  return {
    q: `Can you help with ${c.shortTitle.toLowerCase()}?`,
    a: [
      sentences(c.intro, 2),
      c.approach.length
        ? `At PRAXIS IB the approach is: ${trimEnd(sentences(c.approach[0], 1))}${
            c.approach[1] ? `; ${trimEnd(sentences(c.approach[1], 1))}` : ""
          }.`
        : "",
      alias ? `Also searched for as: ${alias}.` : "",
      `Described in detail on the page “${c.title}” at /beschwerden/${c.slug}.`,
    ]
      .filter(Boolean)
      .join(" "),
  };
};

const enBySlug = new Map(localizedComplaints("en").map((c) => [c.slug, c]));

export const COMPLAINT_FAQ_ENTRIES: ComplaintFaqEntry[] = complaints
  .map((c) => {
    const en = enBySlug.get(c.slug) ?? c;
    const de = buildDe(c);
    const eng = buildEn(en);
    return {
      slug: c.slug,
      label: c.shortTitle,
      labelEn: en.shortTitle,
      q: de.q,
      a: de.a,
      qEn: eng.q,
      aEn: eng.a,
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label, "de"));
